import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Container,
  Pagination,
  Paper,
  Chip,
  Stack,
  Divider,
  Skeleton,
} from "@mui/material";
import {
  Person as PersonIcon,
  SupportAgent as SupportAgentIcon,
  ShoppingBag as BagIcon,
  Favorite as WishlistIcon,
  ShoppingCart as OrdersIcon,
  ArrowForwardIosRounded as ArrowForwardIosRoundedIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Footer from "../Footer/Footer";
import Navbar from "../Navbar/Navbar";
import config from "../../Config";
import ProfileSideBar from "./ProfileSideBar";

const COLORS = {
  cream: "#F7F4EF",
  softCream: "#FBF9F6",
  white: "#FFFFFF",
  black: "#171717",
  mutedBlack: "#3A3A3A",
  grey: "#77736D",
  lightGrey: "#A29D95",
  border: "#DDD7CE",
  gold: "#977743",
  goldLight: "#C4A36A",
  success: "#4E6B50",
  warning: "#9A6A22",
};

const SidebarItem = ({ icon, label, active, onClick }) => (
  <ListItem
    component="button"
    onClick={onClick}
    sx={{
      width: "100%",
      border: 0,
      outline: "none",
      textAlign: "left",
      background: active ? COLORS.black : "transparent",
      color: active ? COLORS.white : COLORS.mutedBlack,
      borderRadius: 0,
      px: 2,
      py: 1.6,
      mb: 0.6,
      cursor: "pointer",
      transition: "all 0.25s ease",
      "&:hover": {
        background: active ? COLORS.black : COLORS.cream,
        color: active ? COLORS.white : COLORS.black,
        transform: "translateX(3px)",
      },
      "& .MuiListItemIcon-root": {
        minWidth: 40,
        color: active ? COLORS.goldLight : COLORS.grey,
        transition: "color 0.25s ease",
      },
    }}
  >
    <ListItemIcon>{icon}</ListItemIcon>

    <ListItemText
      primary={label}
      primaryTypographyProps={{
        fontSize: 13,
        fontWeight: active ? 600 : 500,
        letterSpacing: "0.02em",
      }}
    />

    <ArrowForwardIosRoundedIcon
      sx={{
        fontSize: 12,
        opacity: active ? 0.9 : 0,
        transform: active ? "translateX(0)" : "translateX(-5px)",
        transition: "all 0.25s ease",
      }}
    />
  </ListItem>
);

const getStatusStyles = (status) => {
  const value = String(status || "").toLowerCase();

  if (
    value.includes("cancel") ||
    value.includes("reject") ||
    value.includes("failed")
  ) {
    return {
      color: "#8A3E3E",
      backgroundColor: "#F7E9E7",
      borderColor: "#E8C9C5",
    };
  }

  if (
    value.includes("placed") ||
    value.includes("pending") ||
    value.includes("process")
  ) {
    return {
      color: COLORS.warning,
      backgroundColor: "#F7F0E3",
      borderColor: "#E6D3AD",
    };
  }

  if (
    value.includes("complete") ||
    value.includes("deliver") ||
    value.includes("return")
  ) {
    return {
      color: COLORS.success,
      backgroundColor: "#EAF1EA",
      borderColor: "#C9D8C9",
    };
  }

  return {
    color: COLORS.mutedBlack,
    backgroundColor: COLORS.softCream,
    borderColor: COLORS.border,
  };
};

const OrderSkeleton = () => (
  <Grid item xs={12} md={6}>
    <Card
      elevation={0}
      sx={{
        border: `1px solid ${COLORS.border}`,
        borderRadius: 0,
        overflow: "hidden",
        backgroundColor: COLORS.white,
      }}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 42%" }}>
        <Box sx={{ p: 3 }}>
          <Skeleton width="70%" height={28} />
          <Skeleton width="45%" height={24} sx={{ mt: 1 }} />
          <Skeleton width="35%" height={28} sx={{ mt: 2 }} />
        </Box>

        <Skeleton
          variant="rectangular"
          sx={{
            height: 210,
          }}
        />
      </Box>
    </Card>
  </Grid>
);

export default function MyOrders() {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const userId = sessionStorage.getItem("userId");

  const itemsPerPage = 6;

  const fetchOrders = useCallback(async () => {
    if (!userId) {
      navigate("/Signin");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/${config.getOrders}`,
        {
          headers: {
            "user-id": userId,
            "booking-type": "book",
          },
        },
      );

      setOrders(response.data?.Orders || []);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [navigate, userId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const openAppRedirect = () => {
    const appPackage = "com.ytctechventures.hindustanwardrobe";
    const intentUrl = `intent://ownerProfile?ownerId=${userId}#Intent;scheme=https;package=${appPackage};end`;

    window.location.href = intentUrl;

    setTimeout(() => {
      window.location.href = `https://play.google.com/store/apps/details?id=${appPackage}`;
    }, 1200);
  };

  const paginatedOrders = orders.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage,
  );

  const pageCount = Math.ceil(orders.length / itemsPerPage);

  return (
    <>
      <Navbar />

      <Box
        sx={{
          backgroundColor: COLORS.cream,
          minHeight: "100vh",
          py: { xs: 4, md: 7 },
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "250px minmax(0, 1fr)",
              },
              gap: { xs: 0, md: 5 },
              alignItems: "start",
            }}
          >
            <ProfileSideBar active="orders" />

            <Box sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  mb: { xs: 4, md: 5 },
                  borderBottom: `1px solid ${COLORS.border}`,
                  pb: { xs: 3, md: 4 },
                }}
              >
                <Typography
                  sx={{
                    fontSize: 10,
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    color: COLORS.gold,
                    mb: 1,
                  }}
                >
                  Your wardrobe history
                </Typography>

                <Typography
                  component="h1"
                  sx={{
                    fontFamily: "Georgia, serif",
                    fontSize: {
                      xs: 34,
                      sm: 42,
                      md: 52,
                    },
                    fontWeight: 400,
                    lineHeight: 1.08,
                    color: COLORS.black,
                    mb: 1.5,
                  }}
                >
                  My Orders
                </Typography>

                <Typography
                  sx={{
                    maxWidth: 620,
                    color: COLORS.grey,
                    fontSize: 14,
                    lineHeight: 1.8,
                  }}
                >
                  Keep track of your rentals, booking status and wardrobe
                  selections in one place.
                </Typography>
              </Box>

              {!loading && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr 1fr",
                      sm: "repeat(3, 1fr)",
                    },
                    gap: 1.5,
                    mb: 4,
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      border: `1px solid ${COLORS.border}`,
                      backgroundColor: COLORS.white,
                      borderRadius: 0,
                      p: { xs: 2, md: 2.5 },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 9,
                        textTransform: "uppercase",
                        letterSpacing: "0.14em",
                        color: COLORS.grey,
                        mb: 0.7,
                      }}
                    >
                      Total Orders
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: 28,
                        color: COLORS.black,
                      }}
                    >
                      {orders.length}
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      border: `1px solid ${COLORS.border}`,
                      backgroundColor: COLORS.white,
                      borderRadius: 0,
                      p: { xs: 2, md: 2.5 },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 9,
                        textTransform: "uppercase",
                        letterSpacing: "0.14em",
                        color: COLORS.grey,
                        mb: 0.7,
                      }}
                    >
                      Showing
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: 28,
                        color: COLORS.black,
                      }}
                    >
                      {paginatedOrders.length}
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      border: `1px solid ${COLORS.border}`,
                      backgroundColor: COLORS.black,
                      color: COLORS.white,
                      borderRadius: 0,
                      p: { xs: 2, md: 2.5 },
                      gridColumn: {
                        xs: "1 / -1",
                        sm: "auto",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 9,
                        textTransform: "uppercase",
                        letterSpacing: "0.14em",
                        color: COLORS.goldLight,
                        mb: 0.7,
                      }}
                    >
                      Collection
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: 21,
                        color: COLORS.white,
                      }}
                    >
                      Hindustan Wardrobe
                    </Typography>
                  </Paper>
                </Box>
              )}

              {loading ? (
                <Grid container spacing={2.5}>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <OrderSkeleton key={index} />
                  ))}
                </Grid>
              ) : paginatedOrders.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    border: `1px solid ${COLORS.border}`,
                    backgroundColor: COLORS.white,
                    borderRadius: 0,
                    p: { xs: 4, md: 7 },
                    textAlign: "center",
                  }}
                >
                  <ReceiptLongOutlinedIcon
                    sx={{
                      fontSize: 48,
                      color: COLORS.gold,
                      mb: 2,
                    }}
                  />

                  <Typography
                    sx={{
                      fontFamily: "Georgia, serif",
                      fontSize: 30,
                      color: COLORS.black,
                      mb: 1,
                    }}
                  >
                    No orders yet
                  </Typography>

                  <Typography
                    sx={{
                      color: COLORS.grey,
                      fontSize: 14,
                      lineHeight: 1.8,
                      maxWidth: 450,
                      mx: "auto",
                    }}
                  >
                    Your wardrobe journey starts here. Explore the collection
                    and discover something made for your next occasion.
                  </Typography>

                  <Box
                    component="button"
                    onClick={() => navigate("/AllProduct")}
                    sx={{
                      mt: 3,
                      px: 3.5,
                      py: 1.5,
                      border: `1px solid ${COLORS.black}`,
                      backgroundColor: COLORS.black,
                      color: COLORS.white,
                      textTransform: "uppercase",
                      letterSpacing: "0.12em",
                      fontSize: 11,
                      cursor: "pointer",
                      transition: "all 0.25s ease",
                      "&:hover": {
                        backgroundColor: COLORS.gold,
                        borderColor: COLORS.gold,
                      },
                    }}
                  >
                    Explore Collection
                  </Box>
                </Paper>
              ) : (
                <>
                  <Grid container spacing={2.5}>
                    {paginatedOrders.map((order, index) => {
                      const bookingData = order?.bookingData;
                      const product = bookingData?.productData?.product;
                      const image = bookingData?.productData?.productImage?.[0];
                      const status = bookingData?.bookingStatus;
                      const rent = product?.rentAmount;
                      const title = product?.title || "Wardrobe Item";
                      const bookingId = bookingData?.bookingId;

                      const statusStyles = getStatusStyles(status);

                      return (
                        <Grid item xs={12} md={6} key={bookingId || index}>
                          <Card
                            elevation={0}
                            onClick={() => {
                              if (bookingId) {
                                navigate(`/orderDetail/${bookingId}`);
                              }
                            }}
                            sx={{
                              height: "100%",
                              display: "grid",
                              gridTemplateColumns: {
                                xs: "1fr",
                                sm: "minmax(0, 1fr) 42%",
                              },
                              border: `1px solid ${COLORS.border}`,
                              borderRadius: 0,
                              overflow: "hidden",
                              backgroundColor: COLORS.white,
                              cursor: bookingId ? "pointer" : "default",
                              transition:
                                "transform 0.25s ease, box-shadow 0.25s ease",
                              "&:hover": bookingId
                                ? {
                                    transform: "translateY(-4px)",
                                    boxShadow:
                                      "0 14px 35px rgba(23,23,23,0.10)",
                                  }
                                : {},
                            }}
                          >
                            <CardContent
                              sx={{
                                p: { xs: 2.5, sm: 3 },
                                "&:last-child": {
                                  pb: { xs: 2.5, sm: 3 },
                                },
                                minWidth: 0,
                              }}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                spacing={1}
                                sx={{ mb: 2 }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: 9,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.16em",
                                    color: COLORS.gold,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Rental
                                </Typography>

                                <Chip
                                  label={status || "Pending"}
                                  size="small"
                                  sx={{
                                    height: 26,
                                    borderRadius: 0,
                                    border: `1px solid ${statusStyles.borderColor}`,
                                    color: statusStyles.color,
                                    backgroundColor:
                                      statusStyles.backgroundColor,
                                    fontSize: 10,
                                    fontWeight: 600,
                                    letterSpacing: "0.02em",
                                    maxWidth: "60%",
                                    "& .MuiChip-label": {
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    },
                                  }}
                                />
                              </Stack>

                              <Typography
                                sx={{
                                  fontFamily: "Georgia, serif",
                                  fontSize: {
                                    xs: 23,
                                    sm: 25,
                                  },
                                  lineHeight: 1.2,
                                  color: COLORS.black,
                                  mb: 1.5,
                                  textTransform: "capitalize",
                                }}
                              >
                                {title}
                              </Typography>

                              <Divider
                                sx={{
                                  borderColor: COLORS.border,
                                  mb: 1.8,
                                }}
                              />

                              <Typography
                                sx={{
                                  fontSize: 9,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.14em",
                                  color: COLORS.grey,
                                  mb: 0.4,
                                }}
                              >
                                Rental Price
                              </Typography>

                              <Typography
                                sx={{
                                  fontSize: 20,
                                  fontWeight: 600,
                                  color: COLORS.black,
                                  mb: 2.5,
                                }}
                              >
                                ₹{rent ?? 0}
                              </Typography>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  color: COLORS.grey,
                                }}
                              >
                                <ReceiptLongOutlinedIcon
                                  sx={{
                                    fontSize: 16,
                                    color: COLORS.gold,
                                  }}
                                />

                                <Typography
                                  sx={{
                                    fontSize: 11,
                                    letterSpacing: "0.04em",
                                  }}
                                >
                                  View order details
                                </Typography>

                                <ArrowForwardIosRoundedIcon
                                  sx={{
                                    fontSize: 10,
                                    color: COLORS.lightGrey,
                                  }}
                                />
                              </Box>
                            </CardContent>

                            <CardMedia
                              component="img"
                              image={image || ""}
                              alt={title}
                              sx={{
                                width: "100%",
                                height: {
                                  xs: 230,
                                  sm: "100%",
                                },
                                minHeight: {
                                  xs: 230,
                                  sm: 250,
                                },
                                objectFit: "cover",
                                backgroundColor: COLORS.softCream,
                              }}
                            />
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>

                  {pageCount > 1 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: {
                          xs: "center",
                          md: "flex-start",
                        },
                        mt: 5,
                      }}
                    >
                      <Pagination
                        count={pageCount}
                        page={page}
                        onChange={handlePageChange}
                        shape="rounded"
                        sx={{
                          "& .MuiPaginationItem-root": {
                            borderRadius: 0,
                            border: `1px solid ${COLORS.border}`,
                            color: COLORS.mutedBlack,
                            backgroundColor: COLORS.white,
                          },
                          "& .MuiPaginationItem-root:hover": {
                            backgroundColor: COLORS.cream,
                          },
                          "& .MuiPaginationItem-root.Mui-selected": {
                            backgroundColor: COLORS.black,
                            color: COLORS.white,
                            borderColor: COLORS.black,
                          },
                          "& .MuiPaginationItem-root.Mui-selected:hover": {
                            backgroundColor: COLORS.gold,
                            borderColor: COLORS.gold,
                          },
                        }}
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      <Footer />
    </>
  );
}
